import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../api';
import { MODULES_BY_ROUTE } from '../data/modules';
import { fetchNavigationPreferencesOnce, invalidateNavigationPreferencesCache, navigationPreferencesKey } from './navigationPreferencesCache';

const STORAGE_KEY = 'kp-recent-items';
const MAX_ITEMS = 20;
const SYNC_DEBOUNCE_MS = 800;
const TRACK_DEBOUNCE_MS = 1500;

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
  } catch {}
  return [];
}

function pruneAndPrepend(list, item) {
  const without = list.filter((it) => it.route !== item.route);
  return [item, ...without].slice(0, MAX_ITEMS);
}

function routeToModule(pathname) {
  if (!pathname) return null;
  const exact = MODULES_BY_ROUTE[pathname];
  if (exact) return exact;
  for (const route of Object.keys(MODULES_BY_ROUTE)) {
    if (route === '/' || route.length < 4) continue;
    if (pathname.startsWith(route + '/')) {
      return MODULES_BY_ROUTE[route];
    }
  }
  return null;
}

export function useRecentItems({ enabled = true } = {}) {
  const [items, setItems] = useState(() => readStored());
  const hydratedRef = useRef(false);
  const syncTimerRef = useRef(null);
  const trackTimerRef = useRef(null);
  const location = useLocation();
  // Det senast kända servervärdet, så att uppstarten inte skriver tillbaka
  // det den just hämtat (A11-uppföljning, 2026-10-07).
  const syncedKeyRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await fetchNavigationPreferencesOnce();
      if (cancelled) return;
      if (Array.isArray(data?.recent_items)) {
        const sliced = data.recent_items.slice(0, MAX_ITEMS);
        syncedKeyRef.current = navigationPreferencesKey(sliced);
        setItems(sliced);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(sliced)); } catch {}
      }
      hydratedRef.current = true;
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hydratedRef.current) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch {}
    if (navigationPreferencesKey(items) === syncedKeyRef.current) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      try {
        const prev = (await fetchNavigationPreferencesOnce()) || {};
        await fetch(`${API_URL}/users/me/navigation-preferences`, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            theme: prev.theme || 'light',
            favorites: Array.isArray(prev.favorites) ? prev.favorites : [],
            recent_items: items,
            collapsed_sections: prev.collapsed_sections || { global: [], project: [] },
          }),
        });
        syncedKeyRef.current = navigationPreferencesKey(items);
        invalidateNavigationPreferencesCache();
      } catch {}
    }, SYNC_DEBOUNCE_MS);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [items]);

  useEffect(() => {
    if (!enabled) return undefined;
    const mod = routeToModule(location.pathname);
    if (!mod) return undefined;
    if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    trackTimerRef.current = setTimeout(() => {
      setItems((cur) => pruneAndPrepend(cur, {
        type: 'module',
        route: mod.to,
        label: mod.label,
        iconName: mod.iconName || null,
        ts: Date.now(),
      }));
    }, TRACK_DEBOUNCE_MS);
    return () => {
      if (trackTimerRef.current) clearTimeout(trackTimerRef.current);
    };
  }, [location.pathname, enabled]);

  const clear = useCallback(() => setItems([]), []);

  const getRecent = useCallback((type, limit = 5) => {
    const filtered = type ? items.filter((it) => it.type === type) : items;
    return filtered.slice(0, limit);
  }, [items]);

  return { items, clear, getRecent };
}
